import React, { useEffect, useRef } from "react";
import { Global, Jobs } from "@sourceflow-uk/sourceflow-sdk";
import { SourceFlowHead } from "@sourceflow-uk/sourceflow-head";
import metaObject from "@/.sourceflow/metadata.json";
import sourceflowEnums from "@/sourceflow.enums";
import ReadyCTABanner from "components/ReadyCTABanner";
import JobBanner from "@/components/JobBanner";
import JobDetails from "@/components/JobDetails";
import GoogleJobSchema from "components/Shared/GoogleJobsSchema";
import JobPostingSchema from "components/Shared/GoogleJobPostingSchema";
import { jobSearch } from "@/functions/jobSearch";
import { router } from "@/routes";
import { init, track } from "fbq";

/**
 *
 * @param global
 * @param meta
 * @param page
 * @param job_sector
 * @param job_type
 * @param job_salary_daily
 * @param job_sector_parent
 * @param relatedJobs
 * @returns {Element}
 * @constructor
 */
export default function JobPage({
  global,
  meta,
  page,
  job_sector,
  job_type,
  job_salary_daily,
  job_sector_parent,
  relatedJobs,
}) {
  const trackRef = useRef(false);

  useEffect(() => {
    if (trackRef.current === false) {
      init("574554866614571");
      track("ViewContent", {
        content_ids: [
          `0743ba8e-a6e0-4c4e-93ea-72163b12a2b4_${page.external_reference}`,
        ],
        content_type: "product",
        value: 0.01,
        currency: "EUR",
      });
      trackRef.current = true;
    }
  }, [page.external_reference]);

  return (
    <>
      <SourceFlowHead
        metaObject={metaObject}
        title={meta.title}
        description={meta.description}
        canonical={meta.canonical}
      />
      <div className="bg-light-white">
        <JobBanner
          global={global}
          pathPrefix={`job.page.job_banner`}
          heading={`${page.title}`}
          breadcrumb_title={
            <>
              <span>
                <a href="/jobs" target="_self">
                  Job Search
                </a>
              </span>
              <span className="active">{page.title}</span>
            </>
          }
          return_button_cta_link={`/jobs`}
          return_button_cta_title={`Back to Job Search`}
          cta_title_1={`Apply now`}
          cta_link_1={`#apply-now`}
          post={page}
          job_sector_parent={job_sector_parent}
          job_sector={job_sector}
          job_type={job_type}
          job_salary_daily={job_salary_daily}
        />
        <JobDetails
          global={global}
          pathPrefix={`job.page.job_details`}
          page={page}
          job_heading_description={`Job Description`}
          job_similar_jobs_description={`Similar jobs`}
          job_apply_heading={`Apply today`}
          looking_for_more_heading={`Looking for more?`}
          looking_for_more_content={`Try our Job Search`}
          cta_title_1={`Find more jobs`}
          cta_link_1={`/jobs`}
          relatedJobs={relatedJobs}
        />
        <ReadyCTABanner
          global={global}
          pathPrefix={`job.page.ready_cta_banner`}
          data-color={`light-grey`}
        />

        <JobPostingSchema job={page} job_type={job_type[0]?.name} />
      </div>
    </>
  );
}

/**
 *
 * @returns {Promise<{props: {meta: {description: string, canonical: string, title: string}, jobs: *, global}}>}
 */
export async function getStaticProps({ params: { url_slug } }) {
  const global = (await Global()).toJson();
  const all_jobs = (await Jobs()).map((job) => ({
    ...job,
    url_slug: job.url_slug.replace(/-$/, ""),
  }));
  const page = all_jobs.getJsonItem(url_slug, "url_slug");

  const jobs = all_jobs
    .getItems()
    .filter((job) => new Date(job?.expires_at) >= new Date());
  // .slice(0, 10);
  const currentJob = jobs.find((j) => j.url_slug === url_slug);

  // Job Type
  const jobTypeCategory = currentJob?.categories.find(
    (category) => category.name === "Job Type"
  );
  let jobTypeValues = [];
  if (jobTypeCategory) {
    jobTypeValues = jobTypeCategory.values;
  }

  // Sectors
  const jobSectorsCategory = currentJob?.categories.find(
    (category) => category.name === "Job Sector"
  );
  let jobSectorsValues = [];
  if (jobSectorsCategory) {
    jobSectorsValues = jobSectorsCategory.values;
  }

  // Sectors Parent
  const jobSectorsParentCategory = currentJob?.categories.find(
    (category) => category.name === "Job Sector"
  );
  let jobSectorsParentValues = [];
  if (jobSectorsParentCategory) {
    jobSectorsParentValues = jobSectorsParentCategory.values;
  }

  // Salary Daily
  const jobSalaryDailyCategory = currentJob?.categories.find(
    (category) => category.name === "Salary - Daily"
  );
  const jobSalaryDailyValues =
    jobSalaryDailyCategory && Array.isArray(jobSalaryDailyCategory.values)
      ? jobSalaryDailyCategory.values
      : null;

  const sectorElements = jobSectorsValues.map((sector) => sector.name);

  // Related Jobs
  let relatedJobs;
  // relatedJobs = (await Jobs())
  //   .getItems()
  //   .filter(function (job) {
  //     return (
  //       job.categories
  //         .find((cat) => cat.name === "Job Titles")
  //         ?.values?.some((v) => {
  //           return sectorElements.some(sector => v.name.includes(sector));
  //         })
  //     );
  //   })
  //   .filter((post) =>
  //     post.url_slug !== page.url_slug
  //   )
  //   .filter(function (job) {
  //     return new Date(job?.expires_at) >= new Date();
  //   })
  //   .slice(0, 5);

  // Job Matching by job title & radius (Miles)
  relatedJobs = await jobSearch({
    limit: 6,
    // query: `${page.title}`,
    location: `${page.location}`,
    radius: 50,
  });

  const routerKey = {
    en: router["jobs.item"]["en"].replace("[url_slug]", page.url_slug),
  };

  return {
    props: {
      global,
      page,
      job_sector_parent: jobSectorsParentValues,
      job_sector: jobSectorsValues,
      job_type: jobTypeValues,
      job_salary_daily: jobSalaryDailyValues,
      relatedJobs,
      meta: {
        title: `${page.title} Job ${page?.location ? `in ${page.location}` : ""} | Tradewind Subs`,
        description: `Apply today for ${page.title} job ${page?.location ? `in ${page.location}` : ""}. Explore a variety of job vacancies in your area with Tradewind Subs`,
        // canonical: sourceflowEnums.seo.baseURL,
        canonical: `${sourceflowEnums.seo.baseURL}${routerKey.en}`,
      },
    },
  };
}

export async function getStaticPaths() {
  const all_paths = (await Jobs()).getPaths("url_slug");

  const paths = all_paths.map((path) => ({
    ...path,
    params: { url_slug: path.params.url_slug.replace(/-$/, "") },
  }));

  return { paths, fallback: false };
}
